import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { ExtractionRequestError } from "@/features/data-curation/api/curation";
import { ColumnFields } from "@/features/data-curation/components/ColumnFields";
import {
  useFastFillColumns,
  useUpdateExtractionColumn,
} from "@/features/data-curation/hooks/useCuration";
import { useCurationAgentMessage } from "@/features/data-curation/hooks/useCurationAgentMessage";
import {
  FAST_COLUMN_TYPES,
  changeChecksCells,
  columnsPayload,
  fixColumnMessage,
  hasColumnChanges,
  toColumnDrafts,
  validateColumnDrafts,
  type ColumnDraft,
  type ColumnDraftErrors,
} from "@/features/data-curation/schemaDraft";
import type {
  CurationColumn,
  ExtractionColumnUpdate,
  ExtractionDataset,
} from "@/features/data-curation/types";
import { Check } from "lucide-react";
import { useId, useMemo, useState, type FormEvent } from "react";
import { toast } from "sonner";

type EditColumnDialogProps = {
  dataset: ExtractionDataset;
  /** The column to edit; the dialog is open while it is set. */
  column: CurationColumn | null;
  agentWorking: boolean;
  onClose: () => void;
};

const CHECKBOX_CLASS =
  "mt-0.5 h-4 w-4 shrink-0 rounded-none border-zinc-400 text-zinc-900 focus:ring-2 focus:ring-zinc-900 disabled:opacity-50";

const editErrorMessage = (error: unknown) => {
  if (error instanceof ExtractionRequestError) {
    if (/revision conflict/i.test(error.message)) {
      return "The dataset changed while you were working. Try again.";
    }
    if (error.status === 422) {
      return "The server did not accept this column. Check the label, type, and examples.";
    }
  }
  return error instanceof Error ? error.message : "The column could not be saved.";
};

/** The cells that do not fit the column after the change, or null when all fit. */
const wrongCellsText = ({ mismatched_cells, empty_required_cells }: ExtractionColumnUpdate) => {
  const parts = [
    mismatched_cells > 0 &&
      `${mismatched_cells} ${mismatched_cells === 1 ? "value does" : "values do"} not fit it`,
    empty_required_cells > 0 &&
      `${empty_required_cells} ${empty_required_cells === 1 ? "row has" : "rows have"} no value in it`,
  ].filter((part): part is string => typeof part === "string");
  return parts.length ? parts.join(", and ") : null;
};

type EditColumnFormProps = Omit<EditColumnDialogProps, "column" | "onClose"> & {
  column: CurationColumn;
  updateColumn: ReturnType<typeof useUpdateExtractionColumn>;
  messageAgent: (message: string) => void;
  onCancel: () => void;
  onSaved: () => void;
};

const EditColumnForm = ({
  dataset,
  column,
  agentWorking,
  updateColumn,
  messageAgent,
  onCancel,
  onSaved,
}: EditColumnFormProps) => {
  const id = useId();
  const [draft, setDraft] = useState<ColumnDraft>(() => toColumnDrafts([column])[0]);
  const [askAgent, setAskAgent] = useState(true);
  const [showProblems, setShowProblems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasChat = Boolean(dataset.chat_session_id);
  const problems = useMemo<ColumnDraftErrors>(() => {
    const others = dataset.columns.filter((other) => other.column_id !== column.column_id);
    return validateColumnDrafts([...toColumnDrafts(others), draft]).errors[draft.key] ?? {};
  }, [column.column_id, dataset.columns, draft]);
  const shown = showProblems ? problems : {};
  const changed = hasColumnChanges([draft], [column]);
  const checksCells = changeChecksCells(draft);

  const change = (patch: Partial<ColumnDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));
  const fast = Boolean(dataset.builder);
  const fastFill = useFastFillColumns(dataset.id);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowProblems(true);
    if (!changed || problems.label || problems.examples || problems.options) return;
    setError(null);
    const [payload] = columnsPayload([draft], []);
    const fix = !fast && hasChat && askAgent && !agentWorking && checksCells;
    let result: ExtractionColumnUpdate;
    try {
      result = await updateColumn.mutateAsync({
        columnId: column.column_id,
        expectedRevision: dataset.revision,
        column: payload,
      });
    } catch (saveError) {
      setError(editErrorMessage(saveError));
      return;
    }
    const wrong = wrongCellsText(result);
    if (wrong && fix) messageAgent(fixColumnMessage(dataset.id, payload.label));
    if (fast) fastFill.mutate([column.column_id]);
    toast.success(
      fast
        ? "Column saved. Fast fills it again from the papers it read."
        : !wrong
        ? "Column saved."
        : fix
          ? `Column saved. ${wrong}. The agent fixes them in the chat.`
          : hasChat
            ? `Column saved. ${wrong}. The note in the chat lists them.`
            : `Column saved. ${wrong}.`,
    );
    onSaved();
  };

  return (
    <form noValidate onSubmit={(event) => void submit(event)}>
      <DialogHeader>
        <DialogTitle>Edit the column {column.label}</DialogTitle>
        <DialogDescription>
          The column keeps its cells.
          {hasChat
            ? " A note in the chat tells the agent what you changed, and lists the cells that do not fit the column after the change."
            : ""}
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4 space-y-3">
        <ColumnFields
          id={id}
          draft={draft}
          problems={shown}
          onChange={change}
          allowedTypes={fast ? FAST_COLUMN_TYPES : undefined}
        />
        <label className="flex items-start gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={draft.required}
            onChange={(event) => change({ required: event.target.checked })}
            className={CHECKBOX_CLASS}
          />
          <span>
            Required
            <span className="block text-xs text-zinc-500">
              A row stays pending until it has a value in this column.
            </span>
          </span>
        </label>
        {hasChat && checksCells && (
          <div>
            <label className="flex items-start gap-2 text-sm text-zinc-700">
              <input
                type="checkbox"
                checked={askAgent && !agentWorking}
                disabled={agentWorking}
                onChange={(event) => setAskAgent(event.target.checked)}
                className={CHECKBOX_CLASS}
              />
              Ask the agent in the chat to fix the cells that do not fit
            </label>
            {agentWorking && (
              <p className="mt-1 text-xs text-zinc-500" aria-live="polite">
                The agent is working. The note waits until it stops, and the agent
                reads it on its next turn.
              </p>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="mt-3 rounded-[3px] bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}
      <DialogFooter className="mt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={updateColumn.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          loading={updateColumn.isPending}
          disabled={!changed}
          aria-label={updateColumn.isPending ? "Saving column" : undefined}
        >
          <Check className="h-4 w-4" aria-hidden />
          Save column
        </Button>
      </DialogFooter>
    </form>
  );
};

/**
 * Changes the label, type, definition, or required flag of an approved column.
 * Its cells stay. A note in the chat tells the agent of the change, and lists
 * the cells that do not fit the column after it.
 */
export const EditColumnDialog = ({
  dataset,
  column,
  agentWorking,
  onClose,
}: EditColumnDialogProps) => {
  const updateColumn = useUpdateExtractionColumn(dataset.id);
  const messageAgent = useCurationAgentMessage(dataset.chat_session_id);
  const close = () => {
    updateColumn.reset();
    onClose();
  };
  return (
    <Dialog
      open={column != null}
      onOpenChange={(open) => {
        if (!open && !updateColumn.isPending) close();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        {column && (
          <EditColumnForm
            key={column.column_id}
            dataset={dataset}
            column={column}
            agentWorking={agentWorking}
            updateColumn={updateColumn}
            messageAgent={messageAgent}
            onCancel={close}
            onSaved={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
